import { Link } from 'react-router-dom';
import { useState } from 'react';
import { testimonials } from '@/mocks/comics';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const brandSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '耽耽漫画',
    url: 'https://dandanmanhua.com',
    description: '耽耽漫画在线阅读平台，提供耽耽漫画在线看与免费阅读服务，支持下拉式翻页浏览',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@dandanmanhua.com',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '耽耽漫画是否需要注册才能阅读？',
        acceptedAnswer: { '@type': 'Answer', text: '不需要。耽耽漫画支持免注册在线浏览，打开网站即可开始阅读漫画。注册账号后可享受书架收藏、阅读记录等更多功能。' },
      },
      {
        '@type': 'Question',
        name: '耽耽漫画支持哪些阅读方式？',
        acceptedAnswer: { '@type': 'Answer', text: '耽耽漫画支持下拉式翻页浏览，用户只需向下滚动即可连续阅读，无需频繁点击翻页。同时适配电脑、平板和手机等多种设备。' },
      },
      {
        '@type': 'Question',
        name: '耽耽漫画的更新频率是多少？',
        acceptedAnswer: { '@type': 'Answer', text: '耽耽漫画持续跟踪各类热门漫画的更新动态，确保以最快的速度为读者提供最新章节。热门作品基本保持与官方同步更新。' },
      },
      {
        '@type': 'Question',
        name: '耽耽漫画是否收费？',
        acceptedAnswer: { '@type': 'Answer', text: '耽耽漫画目前所有内容均为免费阅读，无需付费即可浏览全部漫画资源。未来如有变化会提前公告通知。' },
      },
      {
        '@type': 'Question',
        name: '如何在耽耽漫画上找到想看的漫画？',
        acceptedAnswer: { '@type': 'Answer', text: '您可以通过首页的分类导航按类型浏览漫画，也可以使用搜索功能直接查找漫画名称或作者。热门榜单和精选推荐也能帮您发现优质漫画。' },
      },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dandanmanhua.com' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://dandanmanhua.com/about' },
    ],
  };

  const stats = [
    { icon: 'ri-user-line', value: '500万+', label: '月活用户' },
    { icon: 'ri-book-open-line', value: '3万+', label: '漫画资源' },
    { icon: 'ri-thumb-up-line', value: '98%', label: '用户好评率' },
    { icon: 'ri-timer-line', value: '7×24', label: '持续更新' },
  ];

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string)?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch('https://readdy.ai/api/form/d9oo6uqqs38sj6jc7cdg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      const code = (parsed?.code as string) || '';
      const serverMsg = (parsed?.meta as { message?: string })?.message || (parsed?.message as string) || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <div className="min-h-screen pt-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(brandSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
        <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800 whitespace-nowrap">关于我们</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14 text-center">
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
          关于 <span className="text-primary-500">耽耽漫画</span>
        </h1>
        <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
          耽耽漫画是一个专注于提供优质漫画阅读体验的在线平台。我们致力于为漫画爱好者打造一个纯净、便捷、内容丰富的阅读空间，让每一位读者都能在这里找到心仪的漫画作品。
        </p>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pb-10 md:pb-14" aria-labelledby="stats-title">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center p-5 rounded-lg bg-background-100">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-3">
                <i className={`${stat.icon} text-accent-600 text-lg`}></i>
              </div>
              <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">{stat.value}</div>
              <div className="text-xs text-foreground-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Brand Story */}
      <section className="bg-background-100 py-10 md:py-14" aria-labelledby="story-title">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 id="story-title" className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              品牌故事
            </h2>
            <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
              <p>
                耽耽漫画诞生于一群漫画爱好者的共同梦想。我们深知漫画读者对于良好阅读体验的渴望——不想被广告打断、不想频繁翻页、不想为了看一部漫画下载各种应用。于是，耽耽漫画应运而生。
              </p>
              <p>
                我们独创的下拉式阅读模式，让读者只需轻轻向下滑动，就能沉浸在漫画世界中，无需反复点击翻页按钮。从第一话到最新话，流畅的阅读体验让追漫变成一种纯粹的享受。
              </p>
              <p>
                截至目前，耽耽漫画已收录超过三万部各类漫画作品，涵盖耽美、纯爱、玄幻、都市、古风、悬疑、搞笑、热血、百合、奇幻等众多分类。无论你喜欢什么类型的漫画，都能在这里找到属于你的那部神作。
              </p>
              <p>
                我们坚持免费阅读的初心，让每一位读者都能无障碍地享受漫画的乐趣。同时，我们也在不断优化平台体验，提升更新速度，丰富漫画资源，力求成为漫画爱好者的首选阅读平台。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14" aria-labelledby="testimonials-title">
        <h2 id="testimonials-title" className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
          读者口碑
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {testimonials.map((t) => (
            <div key={t.id} className="p-5 rounded-lg bg-background-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                  <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                  <div className="flex items-center gap-0.5 mt-0.5">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="bg-background-100 py-10 md:py-14" aria-labelledby="contact-title">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <h2 id="contact-title" className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="text-center p-4 rounded-lg bg-background-50">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-2">
                  <i className="ri-mail-line text-primary-600 text-lg"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-1">电子邮箱</div>
                <div className="text-sm font-medium text-foreground-800">contact@dandanmanhua.com</div>
              </div>
              <div className="text-center p-4 rounded-lg bg-background-50">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-2">
                  <i className="ri-customer-service-line text-accent-600 text-lg"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-1">在线客服</div>
                <div className="text-sm font-medium text-foreground-800">工作日 9:00 - 18:00</div>
              </div>
              <div className="text-center p-4 rounded-lg bg-background-50">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-2">
                  <i className="ri-question-answer-line text-secondary-600 text-lg"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-1">意见反馈</div>
                <div className="text-sm font-medium text-foreground-800">feedback@dandanmanhua.com</div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-background-50 p-5 md:p-6 rounded-lg">
              <h3 className="text-base font-heading font-semibold text-foreground-900 mb-4 text-center">
                给我们留言
              </h3>
              <form
                onSubmit={handleFormSubmit}
                data-readdy-form="true"
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-1">姓名</label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2 text-sm rounded-lg bg-background-100 border border-background-200 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none transition-colors duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-1">邮箱</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2 text-sm rounded-lg bg-background-100 border border-background-200 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none transition-colors duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-1">留言内容</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-3 py-2 text-sm rounded-lg bg-background-100 border border-background-200 focus:border-primary-400 focus:ring-1 focus:ring-primary-400 outline-none transition-colors duration-200 resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  className="contact-website-alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <button
                  type="submit"
                  className="w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>
                {formStatus === 'success' && (
                  <div className="text-center text-sm text-primary-600 font-medium">
                    留言已发送，感谢您的反馈！
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="text-center text-sm text-accent-600 font-medium">
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14" aria-labelledby="faq-title">
        <h2 id="faq-title" className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
          常见问题
        </h2>
        <div className="max-w-2xl mx-auto space-y-3">
          {faqSchema.mainEntity.map((item, index) => (
            <details key={index} className="group rounded-lg bg-background-100 overflow-hidden">
              <summary className="flex items-center justify-between px-4 py-3 cursor-pointer text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors duration-200">
                {item.name}
                <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
              </summary>
              <div className="px-4 pb-3 text-sm text-foreground-600 leading-relaxed">
                {item.acceptedAnswer.text}
              </div>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}